import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Box } from '@mui/material'

import type { InventoryOrderItem } from '~/api/graphql/generated/types'
import useConfirmAlert from '~/components/common/dialog/useConfirmAlert'
import { LabelValue } from '~/components/common/labels/LabelValue'
import Typography from '~/components/elements/Typography/Typography'
import { ConfirmAlertType } from '~/constants/DialogNames'
import { getPackageType } from '~/store/reducers/constants'

import { getPluralPackageLabel } from '../../../inventoryUtils'

export const useConfirmReceiveOverage = (
  inventoryOrderItem: InventoryOrderItem,
) => {
  const { t } = useTranslation(['Common', 'Admin'])
  const [openConfirmAlert] = useConfirmAlert({
    preventShowAgainCheckBox: false,
    type: ConfirmAlertType.INVENTORY_ORDER_RECEIVE_OVERAGE,
  })
  const PackageType = useSelector(getPackageType)

  const pluralPackageLabel = getPluralPackageLabel(
    inventoryOrderItem.packageType.id,
    PackageType,
  )

  const openConfirmReceiveOverage = ({
    onConfirm,
    totalQuantityOrdered,
    totalQuantityReceived,
  }: {
    onConfirm: (proceed: boolean) => void
    totalQuantityOrdered: number
    totalQuantityReceived: number
  }) => {
    openConfirmAlert({
      sx: {
        '& .MuiDialogContent-root': {
          height: '320px',
          width: '564px',
        },
      },
      applyCustomMessage: true,
      message: (
        <Typography.H3 color="grayGray1">
          {t('Admin:CATALOG.ORDER_ITEM_RECEIPTS_OVERAGE_DIALOG.TITLE')}
        </Typography.H3>
      ),
      content: (
        <Box
          alignItems="center"
          display="flex"
          flexDirection="column"
          gap={2}
          textAlign="center"
        >
          <Box alignItems="center" display="flex" flexDirection="column">
            <Typography.Paragraph color="grayGray1">
              {inventoryOrderItem.inventoryItem.variation?.composedName}
            </Typography.Paragraph>
            <LabelValue
              ValueComponent={Typography.Paragraph}
              label={`${t('Admin:CATALOG.ORDERS.ORDER_ITEM_ORDERED_QUANTITY')}:`}
              value={t('Admin:CATALOG.ORDERS.QUANTITY_PACKAGE_FORMAT', {
                quantity: totalQuantityOrdered,
                packageType: pluralPackageLabel,
                perPackageAmount: inventoryOrderItem.perPackageAmount,
              })}
            />
            <LabelValue
              ValueComponent={Typography.Paragraph}
              label={`${t('Admin:CATALOG.ORDERS.ORDER_ITEM_RECEIVED_QUANTITY')}:`}
              value={t('Admin:CATALOG.ORDERS.QUANTITY_PACKAGE_FORMAT', {
                quantity: totalQuantityReceived,
                packageType: pluralPackageLabel,
                perPackageAmount: inventoryOrderItem.perPackageAmount,
              })}
            />
          </Box>
          <Box mb={1}>
            <Typography.Paragraph color="grayGray1">
              {t(
                'Admin:CATALOG.ORDER_ITEM_RECEIPTS_OVERAGE_DIALOG.ATTEMPTED_TO_RECEIVE_GREATER_THAN_ORDERED',
              )}
            </Typography.Paragraph>
            <Typography.Paragraph color="grayGray1">
              {t(
                'Admin:CATALOG.ORDER_ITEM_RECEIPTS_OVERAGE_DIALOG.DOUBLE_CHECK_QUANTITY_RECEIVED',
              )}
            </Typography.Paragraph>
          </Box>
        </Box>
      ),
      okButtonText: t('Admin:CATALOG.ORDERS.RECEIVE_ACTION'),
      cancelButtonText: t('Common:GO_BACK_AND_EDIT'),
      onConfirm,
    })
  }

  return openConfirmReceiveOverage
}
